import { FC, useEffect, useState } from 'react'
import { Route, Switch, Redirect, useLocation } from 'react-router-dom'
import AgentApplication from './AgentApplication'
import AgentClientType from './AgentClientType'
import AgentLoanComplete from './AgentLoanComplete'
import { toAbsoluteUrl } from '../../../_metronic/helpers'
import { useDispatch, useSelector } from 'react-redux'
import { parse } from 'query-string'
import { getIntegrationURLFromENV } from '../../../utils/getIntegrationURLFromENV'
import axios from 'axios'
import { setFundDetails } from './redux/actions'
import AgentApplicationDashboard from './AgentApplicationDashboard'
import UserDropdown from './UserDropdown'
import { CookieComponent } from '../../../_metronic/assets/ts/components/_CookieComponent'
import { isAuthenticated } from '../auth/auth-utils'

const AgentApplicationWrapper: FC = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const searchQuery: any = parse(location.search)
  const partnerDetails = useSelector((state: any) => state.loan.partner)

  // Check both localStorage flag and KeystoneJS session cookie
  const hasLocalAuth = localStorage.getItem('isAuthorised') === 'true'

  // Check if the session cookie exists and is not expired
  // When a cookie is expired, the browser automatically removes it and get() returns undefined
  const sessionCookie = CookieComponent.get('keystone-agent-js-session')
  const hasValidSessionCookie = !!sessionCookie

  // Only consider user authenticated if both conditions are met
  const isAuthorised = hasLocalAuth && hasValidSessionCookie

  // Track authentication state for the storage event listener
  const [authState, setAuthState] = useState(isAuthorised)

  // Update auth state whenever isAuthorised changes
  useEffect(() => {
    setAuthState(isAuthorised)
  }, [isAuthorised])

  // Set up storage event listener for cross-tab logout synchronization
  useEffect(() => {
    // This function will be triggered when localStorage changes in another tab
    const handleStorageChange = (event: StorageEvent) => {
      // If isAuthorised was removed or logoutTimestamp was updated in another tab
      if (event.key === 'isAuthorised' || event.key === 'logoutTimestamp') {
        // Check current authentication status
        const currentlyAuthenticated = isAuthenticated()

        // If we were authenticated but now we're not, redirect to login with clean URL
        if (authState && !currentlyAuthenticated) {
          // Use replace to create a clean history entry without query parameters
          window.location.replace('/agent-application/login')
        }
      }
    }

    // Add event listener
    window.addEventListener('storage', handleStorageChange)

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [authState])

  useEffect(() => {
    if (partnerDetails?.funds?.length > 0) {
      const selectedFund = partnerDetails.funds[0]
      getFundDetails(selectedFund)
    }
    // eslint-disable-next-line
  }, [partnerDetails?.funds?.length])

  const getFundDetails = async (fundId: string) => {
    const env = localStorage.getItem('env')
    const API_URL = getIntegrationURLFromENV(searchQuery?.env || env)
    const fundData = await axios.get(`${API_URL}ext/api/funds/${fundId || ''}`)

    if (fundData.data && fundData.status === 200) {
      dispatch(setFundDetails(fundData.data))
    }
  }

  return (
    <>
      {isAuthorised && (
        <div className='headerAlexi'>
          <div className='container d-flex justify-content-between'>
            <img alt='Logo' src={toAbsoluteUrl('/media/logos/ccrLogo.png')} />
            {isAuthorised && <UserDropdown />}
          </div>
        </div>
      )}
      <Switch>
        {/* STEP 1: First check if route is the login route */}
        <Route path='/agent-application/login' exact>
          {isAuthorised ? (
            /* If already logged in, redirect to dashboard */
            <Redirect to='/agent-application/view/dashboard' />
          ) : (
            /* Otherwise show login form */
            <AgentClientType />
          )}
        </Route>

        {/* Protected route redirect with return URL functionality */}

        {!isAuthorised && (
          <Route path='/agent-application/:path*'>
            <Redirect
              to={`/agent-application/login?returnTo=${encodeURIComponent(
                location.pathname + location.search
              )}`}
            />
          </Route>
        )}
        {/* STEP 2: For all other routes, require authentication */}
        {isAuthorised ? (
          /* User is authenticated, show protected routes */
          <Switch>
            {/* Protected static routes FIRST */}
            <Route path='/agent-application/view/dashboard' exact>
              <AgentApplicationDashboard />
            </Route>

            <Route path='/agent-application/completed/:id' exact>
              <AgentLoanComplete />
            </Route>

            <Route path='/agent-application/completed' exact>
              <AgentLoanComplete />
            </Route>

            {/* Dynamic routes LAST to prevent overriding */}
            <Route path='/agent-application/:id' exact>
              <AgentApplication />
            </Route>

            <Route path='/agent-application' exact>
              <AgentApplication />
            </Route>

            {/* Default fallback if no routes match */}
            <Redirect to='/agent-application/view/dashboard' />
          </Switch>
        ) : (
          /* User is NOT authenticated, redirect to login */
          <Redirect to='/agent-application/login' />
        )}
      </Switch>
    </>
  )
}

export default AgentApplicationWrapper
